import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AnimatePresence, motion } from "framer-motion";

import PaymentMethodsService from "../../../../../api/paymentMethods";
import {
  cleanSpecialCharacters,
  parseFloatToFixed,
} from "../../../../../extras";

// APP COMPONENTS
import MoneyTextField from "../../../../Inputs/MoneyTextField";
// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Backdrop from "@mui/material/Backdrop";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
// ICONS
import PixIcon from "@mui/icons-material/Pix";
import DrawIcon from "@mui/icons-material/Draw";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import CloseIcon from "@mui/icons-material/Close";
import PercentIcon from "@mui/icons-material/Percent";
import PeopleIcon from "@mui/icons-material/People";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
// COLORS
import {
  blue,
  green,
  grey,
  orange,
  pink,
  purple,
  red,
  teal,
} from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { formatFinance } from "../../../../../utils/formatters";

// ===== Constants =====
// Instead of storing React elements, we store string identifiers
const PAYMENT_METHOD_TYPES = {
  DINHEIRO: "DINHEIRO",
  CREDITO: "CREDITO",
  DEBITO: "DEBITO",
  PIX: "PIX",
  VALE_ALIMENTACAO: "VALE_ALIMENTACAO",
  VALE_REFEICAO: "VALE_REFEICAO",
  VALE_COMBUSTIVEL: "VALE_COMBUSTIVEL",
  VALE_FUNCIONARIO: "VALE_FUNCIONARIO",
  VALE_OUTSIDER: "VALE_OUTSIDER",
};

// ===== Helper Functions =====
// Icon mapping - detached from the data structure
const getPaymentMethodIcon = (methodType) => {
  switch (methodType) {
    case PAYMENT_METHOD_TYPES.DINHEIRO:
      return <LocalAtmIcon />;
    case PAYMENT_METHOD_TYPES.CREDITO:
    case PAYMENT_METHOD_TYPES.DEBITO:
    case PAYMENT_METHOD_TYPES.VALE_ALIMENTACAO:
    case PAYMENT_METHOD_TYPES.VALE_REFEICAO:
      return <CreditCardIcon />;
    case PAYMENT_METHOD_TYPES.VALE_COMBUSTIVEL:
      return <LocalGasStationIcon />;
    case PAYMENT_METHOD_TYPES.VALE_FUNCIONARIO:
      return <NoteAltIcon />;
    case PAYMENT_METHOD_TYPES.PIX:
      return <PixIcon />;
    case PAYMENT_METHOD_TYPES.VALE_OUTSIDER:
    default:
      return <DrawIcon />;
  }
};

// Serializable payment methods definition
const paymentMethods = [
  {
    name: "Dinheiro",
    value: PAYMENT_METHOD_TYPES.DINHEIRO,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.DINHEIRO,
  },
  {
    name: "Crédito",
    value: PAYMENT_METHOD_TYPES.CREDITO,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.CREDITO,
  },
  {
    name: "Débito",
    value: PAYMENT_METHOD_TYPES.DEBITO,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.DEBITO,
  },
  {
    name: "Pix",
    value: PAYMENT_METHOD_TYPES.PIX,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.PIX,
  },
  {
    name: "Vale Alimentação",
    value: PAYMENT_METHOD_TYPES.VALE_ALIMENTACAO,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.VALE_ALIMENTACAO,
  },
  {
    name: "Vale REFEIÇÃO",
    value: PAYMENT_METHOD_TYPES.VALE_REFEICAO,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.VALE_REFEICAO,
  },
  {
    name: "Vale Combustível",
    value: PAYMENT_METHOD_TYPES.VALE_COMBUSTIVEL,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.VALE_COMBUSTIVEL,
  },
  {
    name: "Vale Funcionário",
    value: PAYMENT_METHOD_TYPES.VALE_FUNCIONARIO,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.VALE_FUNCIONARIO,
  },
  {
    name: "Vale Externo",
    value: PAYMENT_METHOD_TYPES.VALE_OUTSIDER,
    selected: false,
    iconType: PAYMENT_METHOD_TYPES.VALE_OUTSIDER,
  },
];

// Currency utilities
const convertPriceToFloat = (formattedPrice) => {
  if (!formattedPrice) return 0;

  // Remove R$ and any non-numeric characters except comma
  const cleanedPrice = formattedPrice
    .replace(/R\$\s*/g, "")
    .replace(/\./g, "")
    .trim();

  // Replace comma with dot for parsing
  const numericPrice = cleanedPrice.replace(",", ".");

  return parseFloat(numericPrice || 0);
};

const formatInitialPrice = (priceValue) => {
  if (!priceValue) return "";
  if (typeof priceValue === "number") {
    return priceValue.toFixed(2).replace(".", ",");
  }
  return priceValue.toString().replace(".", ",");
};

// Array of avatar colors for different persons
const personColors = [
  blue[500],
  purple[500],
  pink[500],
  teal[500],
  orange[500],
  green[500],
];

// ===== Styled Components =====
const ModalContainer = styled(motion.div)(({ theme }) => ({
  position: "absolute",
  top: "5%",
  left: "20%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: "1200px",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fff",
  borderRadius: 12, // Windows 11 medium radius
  boxShadow: "0 8px 24px rgba(0,0,0,0.15)", // Windows 11 elevated shadow
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    width: "85%",
    maxHeight: "90vh",
  },
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 3),
  borderBottom: "1px solid #e2e8f0", // Windows 11 subtle border
  backgroundColor: "#ffffff", // Clean white background
}));

const ModalContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: "auto",
  flex: 1,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
}));

const PaymentMethodCard = styled(motion.div)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  backgroundColor: selected ? blue[50] : "#ffffff",
  color: selected ? blue[700] : grey[700],
  borderRadius: 8, // Windows 11 small radius
  border: selected ? `2px solid ${blue[500]}` : "1px solid #e2e8f0",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  boxShadow: selected
    ? "0 4px 12px rgba(0,0,0,0.12)"
    : "0 2px 8px rgba(0,0,0,0.08)", // Windows 11 shadows
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },
}));

const PercentageButton = styled(motion.div)(({ theme }) => ({
  backgroundColor: "#fff",
  color: grey[800],
  borderRadius: 8, // Windows 11 small radius
  border: "1px solid #e2e8f0",
  padding: theme.spacing(1.5),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    borderColor: blue[500],
  },
}));

const AmountDisplay = styled(motion.div)(({ theme }) => ({
  backgroundColor: "#f8fafc", // Windows 11 grey[50]
  color: grey[800],
  borderRadius: 12, // Windows 11 medium radius
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: "1px solid #e2e8f0",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: grey[800],
}));

const PersonPaymentCard = styled(motion.div)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: 8, // Windows 11 small radius
  border: "1px solid #e2e8f0",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const PaymentProgressBar = styled(motion.div)(({ theme, progress }) => ({
  height: 8,
  backgroundColor: grey[200],
  borderRadius: 10,
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: `${progress}%`,
    backgroundColor:
      progress >= 100 ? green[500] : progress > 75 ? blue[500] : orange[500],
    borderRadius: 10,
    transition: "width 0.5s ease-out",
  },
}));

// ===== Animation Variants =====
const containerVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut",
      when: "beforeChildren",
      staggerChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2 },
  },
};

const splitCardVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: { duration: 0.2 },
  },
};

// ===== Helper Component =====
// Method Icon Component - This improves visualization of selected methods
const MethodIcon = ({ method, selected, onClick, activeColor = blue[500] }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    style={{ position: "relative" }}
  >
    <Tooltip title={method.name}>
      <Card
        onClick={onClick}
        sx={{
          p: 1.5,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          bgcolor: selected ? blue[50] : "white",
          border: selected
            ? `2px solid ${activeColor}`
            : `1px solid ${grey[200]}`,
          borderRadius: 2,
          minWidth: 48, // Ensures consistent sizing
          minHeight: 48,
        }}
      >
        {getPaymentMethodIcon(method.iconType)}

        {/* Selection indicator */}
        {selected && (
          <Box
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              bgcolor: activeColor,
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: "white", fontSize: 14 }} />
          </Box>
        )}
      </Card>
    </Tooltip>
  </motion.div>
);

// ===== Main Component =====
export default function AddPaymentMethodModal({
  showModal,
  closeModal,
  stillToBePaid,
  addPaymentMethod,
}) {
  // Track if modal is closing to prevent multiple submissions
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Local state to control modal visibility
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] =
    useState(showModal);

  const [methods, setMethods] = useState(paymentMethods);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState("");
  const [changeFor, setChangeFor] = useState("");

  // Split payment states
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [numberOfPersons, setNumberOfPersons] = useState(2);
  const [personPayments, setPersonPayments] = useState([]);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(stillToBePaid);

  useEffect(() => {
    loadPaymentMethods().then((r) => r);
  }, []);

  useEffect(() => {
    // Initialize person payments when split is enabled
    if (splitEnabled && personPayments.length === 0) {
      const evenSplitAmount = parseFloat(
        (stillToBePaid / numberOfPersons).toFixed(2)
      );

      // Create initial person payments
      const newPersonPayments = Array.from({ length: numberOfPersons }).map(
        (_, index) => ({
          id: uuidv4(),
          name: `Pessoa ${index + 1}`,
          paymentMethod: null,
          amount: formatInitialPrice(evenSplitAmount),
          changeFor: "",
        })
      );

      setPersonPayments(newPersonPayments);
      calculateTotals(newPersonPayments);
    }
  }, [splitEnabled]);

  // Handle changes in the number of persons
  useEffect(() => {
    if (splitEnabled && personPayments.length > 0) {
      // Always recalculate the even split amount when the number of persons changes
      const evenSplitAmount = parseFloat(
        (stillToBePaid / numberOfPersons).toFixed(2)
      );

      if (numberOfPersons > personPayments.length) {
        // Adding new persons - update ALL persons with the new even amount
        const updatedExistingPayments = personPayments.map((person) => ({
          ...person,
          amount: formatInitialPrice(evenSplitAmount),
        }));

        // Create new person entries
        const additionalPayments = Array.from({
          length: numberOfPersons - personPayments.length,
        }).map((_, index) => ({
          id: uuidv4(),
          name: `Pessoa ${personPayments.length + index + 1}`,
          paymentMethod: null,
          amount: formatInitialPrice(evenSplitAmount),
          changeFor: "",
        }));

        const newPersonPayments = [
          ...updatedExistingPayments,
          ...additionalPayments,
        ];
        setPersonPayments(newPersonPayments);
        calculateTotals(newPersonPayments);
      } else if (numberOfPersons < personPayments.length) {
        // Removing persons - update all remaining persons with the new even amount
        const remainingPersons = personPayments.slice(0, numberOfPersons);
        const updatedRemainingPayments = remainingPersons.map((person) => ({
          ...person,
          amount: formatInitialPrice(evenSplitAmount),
        }));

        setPersonPayments(updatedRemainingPayments);
        calculateTotals(updatedRemainingPayments);
      }
    }
  }, [splitEnabled, numberOfPersons]);

  // Calculate total paid and remaining amounts
  const calculateTotals = (payments = personPayments) => {
    const total = payments.reduce(
      (sum, person) => sum + convertPriceToFloat(person.amount || 0),
      0
    );
    setTotalPaidAmount(total);
    setRemainingAmount(stillToBePaid - total);
  };

  const loadPaymentMethods = async () => {
    try {
      const res = await PaymentMethodsService.getPaymentMethods();
      const customPaymentMethods = res.data.map((paymentMethod) => {
        return {
          name: cleanSpecialCharacters(paymentMethod.title),
          value: paymentMethod.title,
          selected: false,
          iconType: "custom", // Default icon type for custom methods
        };
      });

      setMethods([...methods, ...customPaymentMethods]);
    } catch (error) {
      console.error("Error loading payment methods:", error);
    }
  };

  const calculatePercentage = (percentage) => {
    const calculatedAmount = stillToBePaid * (percentage / 100);
    setAmount(formatInitialPrice(calculatedAmount));
  };

  const handleMethodClick = (clickedMethodValue) => {
    const updatedMethods = methods.map((method) => {
      if (method.value === clickedMethodValue) {
        setSelectedMethod(method);
        return {
          ...method,
          selected: true,
        };
      } else {
        return {
          ...method,
          selected: false,
        };
      }
    });

    setMethods(updatedMethods);
  };

  const handleSubmit = () => {
    if (splitEnabled) {
      // Validate all person payments have methods and amounts
      const isValid = personPayments.every(
        (person) =>
          person.paymentMethod !== null &&
          convertPriceToFloat(person.amount) > 0
      );

      if (!isValid) {
        return alert(
          "Cada pessoa precisa ter um método de pagamento e valor maior que zero"
        );
      }

      // Check if total matches
      if (Math.abs(totalPaidAmount - stillToBePaid) > 0.01) {
        return alert(
          `O valor total (${formatFinance(
            totalPaidAmount
          )}) precisa corresponder ao valor a ser pago (${formatFinance(
            stillToBePaid
          )})`
        );
      }

      // Mark as submitting to prevent multiple submissions
      setIsSubmitting(true);

      // Process each person payment separately
      personPayments.forEach((person) => {
        const personAmountFloat = convertPriceToFloat(person.amount);
        const personChangeForFloat = convertPriceToFloat(person.changeFor || 0);

        // Create a serializable payment method object (no React elements)
        const paymentMethodObj = {
          name: person.paymentMethod.name,
          value: person.paymentMethod.value,
          iconType: person.paymentMethod.iconType, // Store icon type instead of React element
          amount: parseFloatToFixed(personAmountFloat),
          changeFor: parseFloatToFixed(personChangeForFloat),
          id: person.id,
          personName: person.name,
          payment_method: person.paymentMethod.value,
          selected: true,
        };

        // Add each payment method individually
        addPaymentMethod(paymentMethodObj);
      });

      // Close modal after adding all split payments
      setShowAddPaymentMethodModal(false);
      closeModal();
    } else {
      // Original single payment validation
      const amountFloat = convertPriceToFloat(amount);
      const validate = selectedMethod !== null && amountFloat > 0;
      if (!validate) {
        return alert("Selecione um método e defina um valor maior que zero");
      }

      setIsSubmitting(true);

      const changeForFloat = convertPriceToFloat(changeFor);

      // Create a serializable payment method object (no React elements)
      const paymentMethodObj = {
        ...selectedMethod,
        // Don't include React elements in the object going to Redux
        iconType: selectedMethod.iconType, // This is a string identifier
        amount: parseFloatToFixed(amountFloat),
        changeFor: parseFloatToFixed(changeForFloat),
        id: uuidv4(),
        payment_method: selectedMethod.value,
        selected: true,
      };

      addPaymentMethod(paymentMethodObj);

      // Close modal after adding the payment
      setShowAddPaymentMethodModal(false);
      closeModal();
    }
  };

  // Handle payment method selection for a specific person
  const handlePersonMethodSelect = (personIndex, methodValue) => {
    const method = methods.find((m) => m.value === methodValue);

    if (!method) return; // Guard clause

    const updatedPayments = personPayments.map((person, idx) => {
      if (idx === personIndex) {
        return {
          ...person,
          paymentMethod: {
            ...method,
            // Ensure the payment method has all required fields
            payment_method: method.value,
            selected: true,
          },
        };
      }
      return person;
    });

    setPersonPayments(updatedPayments);
  };

  // Handle amount change for a specific person
  const handlePersonAmountChange = (personIndex, newAmount) => {
    const updatedPayments = personPayments.map((person, idx) => {
      if (idx === personIndex) {
        return {
          ...person,
          amount: newAmount,
        };
      }
      return person;
    });

    setPersonPayments(updatedPayments);
    calculateTotals(updatedPayments);
  };

  // Handle change for amount for a specific person (cash payment)
  const handlePersonChangeForAmount = (personIndex, newAmount) => {
    const updatedPayments = personPayments.map((person, idx) => {
      if (idx === personIndex) {
        return {
          ...person,
          changeFor: newAmount,
        };
      }
      return person;
    });

    setPersonPayments(updatedPayments);
  };

  // Handle person name change
  const handlePersonNameChange = (personIndex, newName) => {
    const updatedPayments = personPayments.map((person, idx) => {
      if (idx === personIndex) {
        return {
          ...person,
          name: newName,
        };
      }
      return person;
    });

    setPersonPayments(updatedPayments);
  };

  // Distribute remaining amount among all persons
  const distributeRemaining = () => {
    if (personPayments.length === 0) return;

    // Find persons with payment methods selected
    const eligiblePersons = personPayments.filter(
      (person) => person.paymentMethod !== null
    );

    // If no one has a payment method selected, distribute among all
    const personsToDistribute =
      eligiblePersons.length > 0 ? eligiblePersons : personPayments;

    if (personsToDistribute.length === 0) return;

    const evenShare = parseFloat(
      (remainingAmount / personsToDistribute.length).toFixed(2)
    );

    const updatedPayments = personPayments.map((person) => {
      // Only adjust amounts for persons that should receive a share
      if (personsToDistribute.some((p) => p.id === person.id)) {
        const currentAmount = convertPriceToFloat(person.amount);
        const newAmount = currentAmount + evenShare;
        return {
          ...person,
          amount: formatInitialPrice(newAmount),
        };
      }
      return person;
    });

    setPersonPayments(updatedPayments);
    calculateTotals(updatedPayments);
  };

  // Function to redistribute evenly among all persons
  const redistributeEvenly = () => {
    if (personPayments.length === 0) return;

    const evenSplitAmount = parseFloat(
      (stillToBePaid / personPayments.length).toFixed(2)
    );

    const updatedPayments = personPayments.map((person) => ({
      ...person,
      amount: formatInitialPrice(evenSplitAmount),
    }));

    // Handle any rounding discrepancy by adjusting the first person's amount
    const totalAfterSplit = updatedPayments.reduce(
      (sum, person) => sum + convertPriceToFloat(person.amount),
      0
    );
    const roundingDifference = parseFloat(
      (stillToBePaid - totalAfterSplit).toFixed(2)
    );

    if (Math.abs(roundingDifference) > 0.01) {
      const firstPersonAmount = convertPriceToFloat(updatedPayments[0].amount);
      updatedPayments[0].amount = formatInitialPrice(
        firstPersonAmount + roundingDifference
      );
    }

    setPersonPayments(updatedPayments);
    calculateTotals(updatedPayments);
  };

  // Balance payments to match total
  const balancePayments = () => {
    if (personPayments.length === 0) return;

    const difference = parseFloat((stillToBePaid - totalPaidAmount).toFixed(2));
    if (Math.abs(difference) < 0.01) return; // Already balanced

    // Try to find a person with a payment method already selected
    const personsWithMethod = personPayments.filter(
      (person) => person.paymentMethod !== null
    );

    // If there's a person with a payment method, use the first one
    // Otherwise, use the first person in the list
    const targetPersonIndex =
      personsWithMethod.length > 0
        ? personPayments.findIndex(
            (person) => person.id === personsWithMethod[0].id
          )
        : 0;

    // Add the difference to the target person's payment
    const updatedPayments = [...personPayments];
    const targetPersonAmount = convertPriceToFloat(
      updatedPayments[targetPersonIndex].amount
    );
    updatedPayments[targetPersonIndex] = {
      ...updatedPayments[targetPersonIndex],
      amount: formatInitialPrice(targetPersonAmount + difference),
    };

    setPersonPayments(updatedPayments);
    calculateTotals(updatedPayments);
  };

  // Sync local modal state with parent's modal state
  useEffect(() => {
    setShowAddPaymentMethodModal(showModal);
  }, [showModal]);

  // Handle modal close with proper cleanup
  const handleModalClose = () => {
    if (isSubmitting) return; // Prevent closing during submission
    setShowAddPaymentMethodModal(false);
    closeModal();
  };

  // Function to check if a method is selected for a specific person
  const isMethodSelectedForPerson = (personIndex, methodValue) => {
    const person = personPayments[personIndex];
    return (
      person &&
      person.paymentMethod &&
      person.paymentMethod.value === methodValue
    );
  };

  return (
    <Modal
      open={showAddPaymentMethodModal}
      onClose={handleModalClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backdropFilter: "blur(4px)" },
      }}
      sx={{ zIndex: 9999999 }}
    >
      <AnimatePresence mode="wait">
        {showModal && (
          <ModalContainer
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <ModalHeader>
              <Stack direction="row" spacing={1} alignItems="center">
                <PaymentIcon sx={{ color: blue[500] }} />
                <Typography variant="h6" fontWeight="600">
                  Adicionar Método de Pagamento
                </Typography>
              </Stack>
              <motion.div
                whileHover={{ rotate: 90 }}
                transition={{ duration: 0.2 }}
              >
                <IconButton onClick={closeModal} size="small">
                  <CloseIcon />
                </IconButton>
              </motion.div>
            </ModalHeader>

            <ModalContent>
              {/* Payment Split Toggle */}
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 3,
                    border: `1px solid ${splitEnabled ? blue[100] : grey[200]}`,
                    backgroundColor: splitEnabled ? blue[50] : "white",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <PeopleIcon color={splitEnabled ? "primary" : "action"} />
                      <div>
                        <Typography variant="subtitle1" fontWeight={500}>
                          Dividir pagamento
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Habilite para dividir o pagamento entre várias pessoas
                        </Typography>
                      </div>
                    </Stack>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={splitEnabled}
                          onChange={(e) => setSplitEnabled(e.target.checked)}
                          color="primary"
                        />
                      }
                      label=""
                    />
                  </Stack>

                  <AnimatePresence>
                    {splitEnabled && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Divider sx={{ my: 2 }} />
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          alignItems="center"
                        >
                          <Typography variant="body2">
                            Número de pessoas:
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              border: `1px solid ${grey[300]}`,
                              borderRadius: 2,
                              px: 2,
                              py: 0.5,
                              width: { xs: "100%", sm: "auto" },
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                setNumberOfPersons(
                                  Math.max(2, numberOfPersons - 1)
                                )
                              }
                              disabled={numberOfPersons <= 2}
                            >
                              <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                            <Typography
                              sx={{ minWidth: 30, textAlign: "center" }}
                            >
                              {numberOfPersons}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setNumberOfPersons(
                                  Math.min(6, numberOfPersons + 1)
                                )
                              }
                              disabled={numberOfPersons >= 6}
                            >
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>

                          <Tooltip title="Valor por pessoa">
                            <Chip
                              icon={<ReceiptLongIcon />}
                              label={`${formatFinance(
                                stillToBePaid / numberOfPersons
                              )} por pessoa`}
                              variant="outlined"
                              color="primary"
                            />
                          </Tooltip>
                        </Stack>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </motion.div>

              <AnimatePresence>
                {!splitEnabled ? (
                  // Single Payment Method UI
                  <Grid container spacing={4}>
                    {/* Payment Methods Section */}
                    <Grid item xs={12} md={6}>
                      <motion.div variants={itemVariants}>
                        <SectionTitle variant="h6">
                          Selecione o Método de Pagamento
                        </SectionTitle>
                      </motion.div>

                      <Grid container spacing={2}>
                        {methods.map((method) => (
                          <Grid item xs={6} sm={4} key={method.value}>
                            <motion.div
                              variants={itemVariants}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <PaymentMethodCard
                                selected={method.selected}
                                onClick={() => handleMethodClick(method.value)}
                              >
                                <motion.div
                                  whileHover={{ scale: 1.2 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 400,
                                  }}
                                  style={{ fontSize: 32, marginBottom: 8 }}
                                >
                                  {getPaymentMethodIcon(method.iconType)}
                                </motion.div>
                                <Typography
                                  variant="body2"
                                  align="center"
                                  fontWeight={method.selected ? 600 : 400}
                                >
                                  {method.name}
                                </Typography>
                              </PaymentMethodCard>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* Payment Values Section */}
                    <Grid item xs={12} md={6}>
                      <motion.div variants={itemVariants}>
                        <SectionTitle variant="h6">Valores</SectionTitle>
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <AmountDisplay>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <div>
                              <Typography variant="body2" color={blue[600]}>
                                Total a pagar
                              </Typography>
                              <Typography
                                variant="h4"
                                fontWeight="700"
                                color={blue[900]}
                              >
                                {formatFinance(stillToBePaid)}
                              </Typography>
                            </div>
                            <motion.div
                              animate={{ rotate: [0, 5, 0] }}
                              transition={{
                                repeat: Infinity,
                                duration: 2,
                                repeatType: "reverse",
                              }}
                            >
                              <CreditCardIcon
                                sx={{ fontSize: 40, color: blue[400] }}
                              />
                            </motion.div>
                          </Stack>
                        </AmountDisplay>
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <Typography
                          variant="body2"
                          color={grey[600]}
                          sx={{ mb: 2 }}
                        >
                          Selecione uma porcentagem ou insira um valor
                          personalizado
                        </Typography>

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={4}>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <PercentageButton
                                onClick={() => calculatePercentage(100)}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <PercentIcon fontSize="small" />
                                  <Typography fontWeight={500}>100%</Typography>
                                </Stack>
                              </PercentageButton>
                            </motion.div>
                          </Grid>
                          <Grid item xs={4}>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <PercentageButton
                                onClick={() => calculatePercentage(50)}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <PercentIcon fontSize="small" />
                                  <Typography fontWeight={500}>50%</Typography>
                                </Stack>
                              </PercentageButton>
                            </motion.div>
                          </Grid>
                          <Grid item xs={4}>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <PercentageButton
                                onClick={() => calculatePercentage(33)}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <PercentIcon fontSize="small" />
                                  <Typography fontWeight={500}>33%</Typography>
                                </Stack>
                              </PercentageButton>
                            </motion.div>
                          </Grid>
                        </Grid>
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <Stack spacing={3}>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ mb: 1 }}
                            >
                              Valor
                            </Typography>
                            <MoneyTextField
                              id="payment-amount"
                              label="Valor a ser pago"
                              value={amount}
                              setValue={setAmount}
                              fullWidth={true}
                            />
                          </Box>

                          <AnimatePresence>
                            {selectedMethod?.value === "DINHEIRO" && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{ mb: 1 }}
                                >
                                  Troco para
                                </Typography>
                                <MoneyTextField
                                  id="change-for-amount"
                                  label="Valor para troco"
                                  value={changeFor}
                                  setValue={setChangeFor}
                                  fullWidth={true}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Stack>
                      </motion.div>
                    </Grid>
                  </Grid>
                ) : (
                  // Split Payment UI
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Grid container spacing={3}>
                      {/* Split Payment Summary */}
                      <Grid item xs={12}>
                        <motion.div variants={itemVariants}>
                          <Card
                            sx={{
                              p: 3,
                              mb: 3,
                              borderRadius: 3,
                              backgroundColor: grey[50],
                              border: `1px solid ${grey[200]}`,
                            }}
                          >
                            <Stack
                              direction={{ xs: "column", md: "row" }}
                              spacing={3}
                              justifyContent="space-between"
                              alignItems={{ xs: "stretch", md: "center" }}
                            >
                              <div>
                                <Typography
                                  variant="subtitle2"
                                  color={grey[600]}
                                >
                                  Total a pagar
                                </Typography>
                                <Typography
                                  variant="h4"
                                  fontWeight="700"
                                  color={blue[700]}
                                >
                                  {formatFinance(stillToBePaid)}
                                </Typography>
                              </div>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  sx={{ mb: 0.5 }}
                                >
                                  <Typography variant="body2" color={grey[600]}>
                                    Progresso do pagamento
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    color={
                                      Math.abs(remainingAmount) < 0.01
                                        ? green[600]
                                        : remainingAmount > 0
                                        ? orange[700]
                                        : red[500]
                                    }
                                  >
                                    {totalPaidAmount > 0
                                      ? `${(
                                          (totalPaidAmount / stillToBePaid) *
                                          100
                                        ).toFixed(0)}%`
                                      : "0%"}
                                  </Typography>
                                </Stack>
                                <PaymentProgressBar
                                  progress={Math.min(
                                    100,
                                    (totalPaidAmount / stillToBePaid) * 100
                                  )}
                                />
                              </Box>

                              <div>
                                <Typography
                                  variant="subtitle2"
                                  color={grey[600]}
                                >
                                  {remainingAmount > 0
                                    ? "Valor restante"
                                    : "Valor excedente"}
                                </Typography>
                                <Typography
                                  variant="h5"
                                  fontWeight="700"
                                  color={
                                    Math.abs(remainingAmount) < 0.01
                                      ? green[600]
                                      : remainingAmount > 0
                                      ? orange[700]
                                      : red[500]
                                  }
                                >
                                  {formatFinance(Math.abs(remainingAmount))}
                                </Typography>
                              </div>

                              <Stack direction="row" spacing={1}>
                                <Tooltip title="Dividir igualmente entre todos">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={redistributeEvenly}
                                    startIcon={<PeopleIcon />}
                                  >
                                    Dividir Igual
                                  </Button>
                                </Tooltip>

                                {remainingAmount !== 0 && (
                                  <>
                                    <Tooltip title="Distribuir valor restante">
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={distributeRemaining}
                                        disabled={remainingAmount === 0}
                                      >
                                        Distribuir
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Ajustar valor para total">
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={balancePayments}
                                        disabled={
                                          remainingAmount === 0 ||
                                          personPayments.length === 0
                                        }
                                        color="success"
                                      >
                                        Ajustar
                                      </Button>
                                    </Tooltip>
                                  </>
                                )}
                              </Stack>
                            </Stack>
                          </Card>
                        </motion.div>
                      </Grid>

                      {/* Person Payment Cards */}
                      <Grid item xs={12}>
                        <AnimatePresence>
                          {personPayments.map((person, index) => (
                            <PersonPaymentCard
                              key={person.id}
                              variants={splitCardVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                            >
                              <Grid container spacing={2}>
                                {/* Person Identity */}
                                <Grid item xs={12} sm={4} md={3}>
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor:
                                          personColors[
                                            index % personColors.length
                                          ],
                                        width: 40,
                                        height: 40,
                                      }}
                                    >
                                      {person.name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <TextField
                                      variant="standard"
                                      value={person.name}
                                      onChange={(e) =>
                                        handlePersonNameChange(
                                          index,
                                          e.target.value
                                        )
                                      }
                                      placeholder="Nome"
                                      fullWidth
                                    />
                                  </Stack>
                                </Grid>

                                {/* Payment Method */}
                                <Grid item xs={12} sm={8} md={4}>
                                  <Typography
                                    variant="body2"
                                    color={grey[600]}
                                    sx={{ mb: 1 }}
                                  >
                                    Método de Pagamento
                                  </Typography>

                                  {/* Show selected method prominently */}
                                  {person.paymentMethod && (
                                    <Box mb={2}>
                                      <Chip
                                        icon={getPaymentMethodIcon(
                                          person.paymentMethod.iconType
                                        )}
                                        label={person.paymentMethod.name}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: "bold" }}
                                      />
                                    </Box>
                                  )}

                                  <Box
                                    sx={{
                                      maxHeight: "180px",
                                      overflowY: "auto",
                                      pr: 1,
                                    }}
                                  >
                                    <Grid container spacing={1}>
                                      {methods.map((method) => (
                                        <Grid
                                          item
                                          xs={3}
                                          key={`${person.id}-${method.value}`}
                                        >
                                          <MethodIcon
                                            method={method}
                                            selected={isMethodSelectedForPerson(
                                              index,
                                              method.value
                                            )}
                                            activeColor={
                                              personColors[
                                                index % personColors.length
                                              ]
                                            }
                                            onClick={() =>
                                              handlePersonMethodSelect(
                                                index,
                                                method.value
                                              )
                                            }
                                          />
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>

                                  {/* Helper message if no method selected */}
                                  {!person.paymentMethod && (
                                    <Alert
                                      severity="info"
                                      sx={{ mt: 1, py: 0 }}
                                    >
                                      Selecione um método de pagamento
                                    </Alert>
                                  )}
                                </Grid>

                                {/* Payment Amount */}
                                <Grid item xs={12} sm={12} md={5}>
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={2}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="body2"
                                        color={grey[600]}
                                        sx={{ mb: 1 }}
                                      >
                                        Valor de Pagamento
                                      </Typography>
                                      <MoneyTextField
                                        id={`person-${index}-amount`}
                                        label="Valor"
                                        value={person.amount}
                                        setValue={(value) =>
                                          handlePersonAmountChange(index, value)
                                        }
                                        fullWidth={true}
                                      />
                                    </Box>

                                    <AnimatePresence>
                                      {person.paymentMethod?.value ===
                                        "DINHEIRO" && (
                                        <motion.div
                                          initial={{ opacity: 0, width: 0 }}
                                          animate={{
                                            opacity: 1,
                                            width: "auto",
                                          }}
                                          exit={{ opacity: 0, width: 0 }}
                                          style={{ flex: 1 }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color={grey[600]}
                                            sx={{ mb: 1 }}
                                          >
                                            Troco para
                                          </Typography>
                                          <MoneyTextField
                                            id={`person-${index}-change-for`}
                                            label="Troco"
                                            value={person.changeFor || ""}
                                            setValue={(value) =>
                                              handlePersonChangeForAmount(
                                                index,
                                                value
                                              )
                                            }
                                            fullWidth={true}
                                          />
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </PersonPaymentCard>
                          ))}
                        </AnimatePresence>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer with Action Buttons */}
              <motion.div variants={itemVariants} style={{ marginTop: 32 }}>
                <Divider sx={{ mb: 3 }} />
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={closeModal}
                      sx={{
                        borderRadius: 8,
                        px: 3,
                        py: 1,
                        borderColor: grey[300],
                        color: grey[700],
                      }}
                    >
                      Voltar
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{
                        borderRadius: 8,
                        px: 3,
                        py: 1,
                        backgroundColor: green[600],
                        "&:hover": {
                          backgroundColor: green[700],
                        },
                      }}
                    >
                      Confirmar
                    </Button>
                  </motion.div>
                </Stack>
              </motion.div>
            </ModalContent>
          </ModalContainer>
        )}
      </AnimatePresence>
    </Modal>
  );
}
